import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ArrowLeft, Inbox, MailCheck, Archive, MoreHorizontal, Trash2, Phone, Mail, LogOut, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusMeta = {
  new: { label: "Nouveau", className: "bg-orange-700 text-stone-50 hover:bg-orange-800" },
  contacted: { label: "Contacté", className: "bg-stone-900 text-stone-50 hover:bg-stone-800" },
  archived: { label: "Archivé", className: "bg-stone-200 text-stone-700 hover:bg-stone-300" },
};

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([
        api.get(`/quotes`),
        api.get(`/quotes/stats`),
      ]);
      setQuotes(q.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}`, { status });
      toast.success("Statut mis à jour");
      fetchAll();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const removeQuote = async (id) => {
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Demande supprimée");
      setSelected(null);
      fetchAll();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const handleExportCsv = async () => {
    try {
      const res = await api.get(`/quotes/export.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "text/csv;charset=utf-8" })
      );
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `sad-demandes-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch {
      toast.error("Erreur lors de l'export");
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-stone-50 text-stone-900">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-stone-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="back-to-landing"
              className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Retour
            </Link>
            <span className="h-5 w-px bg-stone-200" />
            <span className="flex items-center gap-2" translate="no">
              <img src="/logo-sad.svg?v=14" alt="Logo SAD" className="h-9 w-auto object-contain" />
              <p className="text-sm uppercase tracking-[0.2em] text-stone-500">Admin</p>
            </span>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <div data-testid="admin-user-badge" className="hidden items-center gap-2 sm:flex">
                {user.picture && (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="h-7 w-7 rounded-full border border-stone-200 object-cover"
                  />
                )}
                <span className="text-xs text-stone-600">{user.email}</span>
              </div>
            )}
            <Button
              data-testid="logout-button"
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="rounded-full"
            >
              <LogOut className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="mb-10 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-light tracking-tight sm:text-4xl">Tableau de bord</h1>
            <p className="mt-1 text-sm text-stone-500">
              Suivi des demandes reçues depuis le site SAD.
            </p>
          </div>
          <div className="flex gap-2 self-start sm:self-auto">
            <Button
              data-testid="refresh-quotes"
              variant="outline"
              onClick={fetchAll}
            >
              Actualiser
            </Button>
            <Button
              data-testid="export-csv-button"
              onClick={handleExportCsv}
              disabled={quotes.length === 0}
              className="bg-stone-900 text-stone-50 hover:bg-orange-800"
            >
              <Download className="mr-2 h-4 w-4" strokeWidth={1.5} />
              Exporter CSV
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-10 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard label="Total" value={stats.total} icon={<Inbox className="h-4 w-4" strokeWidth={1.5} />} testid="stat-total" />
          <StatCard label="Nouveaux" value={stats.new} icon={<Inbox className="h-4 w-4 text-orange-700" strokeWidth={1.5} />} testid="stat-new" highlight />
          <StatCard label="Contactés" value={stats.contacted} icon={<MailCheck className="h-4 w-4" strokeWidth={1.5} />} testid="stat-contacted" />
          <StatCard label="Archivés" value={stats.archived} icon={<Archive className="h-4 w-4" strokeWidth={1.5} />} testid="stat-archived" />
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
          <Table data-testid="quotes-table">
            <TableCaption className="pb-4">
              {loading ? "Chargement…" : `${quotes.length} demande(s) au total`}
            </TableCaption>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Date</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Projet</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Délai</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} className="py-16 text-center text-stone-400">
                    Aucune demande pour le moment.
                  </TableCell>
                </TableRow>
              )}
              {quotes.map((q) => (
                <TableRow key={q.id} data-testid={`quote-row-${q.id}`} className="hover:bg-stone-50">
                  <TableCell className="text-sm text-stone-500">{formatDate(q.created_at)}</TableCell>
                  <TableCell>
                    <div className="font-medium text-stone-900">{q.nom}</div>
                    <div className="text-xs text-stone-500">{q.email}</div>
                  </TableCell>
                  <TableCell className="text-sm">{q.type_projet}</TableCell>
                  <TableCell className="text-sm">{q.budget}</TableCell>
                  <TableCell className="text-sm">{q.delai}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs ${statusMeta[q.status]?.className}`}
                    >
                      {statusMeta[q.status]?.label || q.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`view-quote-${q.id}`}
                        onClick={() => setSelected(q)}
                      >
                        Voir
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" data-testid={`menu-quote-${q.id}`}>
                            <MoreHorizontal className="h-4 w-4" strokeWidth={1.5} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => updateStatus(q.id, "new")}>
                            Marquer Nouveau
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(q.id, "contacted")}>
                            Marquer Contacté
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(q.id, "archived")}>
                            Archiver
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => removeQuote(q.id)}
                            data-testid={`delete-quote-${q.id}`}
                          >
                            <Trash2 className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="quote-detail-dialog" className="max-w-xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-light tracking-tight">
                  Demande de {selected.nom}
                </DialogTitle>
                <DialogDescription>
                  Reçue le {formatDate(selected.created_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Email" value={<a className="hover:underline" href={`mailto:${selected.email}`}><Mail className="mr-1 inline h-3.5 w-3.5" strokeWidth={1.5} />{selected.email}</a>} />
                  <Field label="Téléphone" value={<a className="hover:underline" href={`tel:${selected.telephone}`}><Phone className="mr-1 inline h-3.5 w-3.5" strokeWidth={1.5} />{selected.telephone}</a>} />
                  <Field label="Type de projet" value={selected.type_projet} />
                  <Field label="Budget" value={selected.budget} />
                  <Field label="Délai" value={selected.delai} />
                  <Field label="Statut" value={statusMeta[selected.status]?.label} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-stone-500">Message</div>
                  <p className="mt-2 whitespace-pre-wrap rounded-md border border-stone-200 bg-stone-50 p-4 text-stone-700">
                    {selected.message || "—"}
                  </p>
                </div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" onClick={() => updateStatus(selected.id, "contacted")}>Marquer Contacté</Button>
                <Button variant="outline" onClick={() => updateStatus(selected.id, "archived")}>Archiver</Button>
                <Button className="bg-orange-700 hover:bg-orange-800" onClick={() => setSelected(null)}>Fermer</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatCard = ({ label, value, icon, testid, highlight }) => (
  <div
    data-testid={testid}
    className={`rounded-xl border bg-white p-5 ${highlight ? "border-orange-200" : "border-stone-200"}`}
  >
    <div className="flex items-center justify-between text-stone-500">
      <span className="text-xs uppercase tracking-[0.18em]">{label}</span>
      {icon}
    </div>
    <div className="mt-3 text-3xl font-light tracking-tight text-stone-900 tick">{value}</div>
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.18em] text-stone-500">{label}</div>
    <div className="mt-1 text-stone-800">{value}</div>
  </div>
);
